import React, { useEffect, useRef } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { classNames } from "maven-lib/dist/utils/misc";

interface EcoaInputProps {
  className?: string;
  multiline?: boolean;
  placeholder?: string;
  defaultVal?: string | number | null | undefined;
  isTextFocusOn?: boolean;
  disabled?: boolean;
  prefix?: string;
  suffix?: string;
  onValueChange: (value: number | string | null, ev?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onClickEvent?: (ev: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement, MouseEvent>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

export function EcoaInput(props: EcoaInputProps) {
  const {
    className,
    multiline,
    placeholder,
    isTextFocusOn: isTextOnFocus,
    defaultVal,
    disabled,
    prefix,
    suffix,
    onValueChange,
    onClickEvent,
    onBlur,
  } = props;
  const typeTextInputFocus = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isTextOnFocus) {
      typeTextInputFocus.current?.focus();
    }
  }, [isTextOnFocus]);

  const ecoaInputClassNames: Array<string> = [];

  if (prefix) ecoaInputClassNames.push("--with-prefix");
  if (suffix) ecoaInputClassNames.push("--with-suffix");

  return (
    <StyledInputContainer className={classNames(className, ecoaInputClassNames)}>
      {!!prefix && (
        <StyledAffixWrapper className={classNames("--prefix")}>
          <span>{prefix}</span>
        </StyledAffixWrapper>
      )}
      {!multiline && (
        <StyledInput
          ref={typeTextInputFocus}
          disabled={disabled}
          value={defaultVal || ""}
          placeholder={placeholder}
          onClick={(ev) => onClickEvent && onClickEvent(ev)}
          onChange={(ev) => onValueChange(ev.currentTarget.value, ev)}
          onBlur={(e) => onBlur && onBlur(e)}
        />
      )}
      {multiline && (
        <StyledTextArea
          disabled={disabled}
          value={defaultVal || ""}
          placeholder={placeholder}
          onClick={(ev) => onClickEvent && onClickEvent(ev)}
          onChange={(ev) => onValueChange(ev.currentTarget.value, ev)}
          onBlur={(e) => onBlur && onBlur(e)}
        />
      )}
      {!!suffix && (
        <StyledAffixWrapper className={classNames("--suffix")}>
          <span>{suffix}</span>
        </StyledAffixWrapper>
      )}
    </StyledInputContainer>
  );
}

const StyledInputContainer = styled.div`
  display: flex;
  flex-direction: row;
  border: 2px solid rgba(0, 0, 0, 0.03);

  &:focus-within {
    border: 2px solid #259ac5;
    outline: none;
  }
`;

const StyledAffixWrapper = styled.div`
  display: flex;
  align-items: center;
  background: #f0f0f0;
  word-break: keep-all;
  padding: 0px 10px;
`;

const commonInputStyle = css`
  width: 100%;
  padding: 0.2rem 0.4rem;
  border: 2px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.03);
  font-size: 22px;
  appearance: none;
  border: none;
  &::placeholder {
    color: #bbb;
  }
  &:disabled {
    background: #f0f0f0;
  }
`;

const StyledInput = styled.input`
  ${commonInputStyle}
`;

const StyledTextArea = styled.textarea`
  ${commonInputStyle}

  height: calc(40vh);
  min-height: 150px;
  max-height: 700px;
  resize: vertical;
`;
